/**
 * HARDENING V1 (LOT 4) — PROPRIÉTÉ DU SEO PAR PAGE (anti-doublon Helmet).
 *
 * L'audit a prouvé que react-helmet-async NE déduplique PAS les balises
 * entre instances Helmet distinctes : quand une page rendait son propre
 * <SEOHead> en plus des défauts du layout (PublicSeoDefaults), le <head>
 * exposait DEUX meta descriptions (et jusqu'à deux canonicals différents
 * avec l'ancien LanguageMeta) — métadonnées invalides pour les moteurs.
 *
 * Principe : chaque <SEOHead> de PAGE s'enregistre comme « propriétaire »
 * du SEO de la route courante. PublicSeoDefaults (layout) consulte ce
 * registre et SE TAIT dès qu'un propriétaire est monté. Une route publique
 * expose ainsi EXACTEMENT une série de balises (title, description,
 * canonical, hreflang, robots, OG) — verrouillé par
 * backend/tests/test_hardening_lot4_seo.py sur les snapshots prerender.
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const SeoOwnershipContext = createContext(null);

export function SeoOwnershipProvider({ children }) {
  const [owners, setOwners] = useState(0);
  const register = useCallback(() => {
    setOwners((n) => n + 1);
    return () => setOwners((n) => Math.max(0, n - 1));
  }, []);
  return (
    <SeoOwnershipContext.Provider value={{ owners, register }}>
      {children}
    </SeoOwnershipContext.Provider>
  );
}

/** Appelé par SEOHead (pages uniquement, pas les défauts du layout). */
export function usePageSeoRegistration(enabled) {
  const ctx = useContext(SeoOwnershipContext);
  useEffect(() => {
    if (!enabled || !ctx) return undefined;
    return ctx.register();
  }, [enabled, ctx]);
}

/** Consulté par PublicSeoDefaults : true si une page possède déjà son SEO. */
export function useHasPageSeoOwner() {
  const ctx = useContext(SeoOwnershipContext);
  return ctx ? ctx.owners > 0 : false;
}
